import { Edit, Shield, ShieldOff, Trash } from 'react-feather';
import { Fragment } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client';

import {
  GetUsersForProjectQuery as GetUsersForProject,
  Permission,
} from '__generated__/graphql';
import { gql } from '__generated__';

import { GetSessionQuery, useSession } from '../Auth/SessionProvider';
import { isGraphQLAuthenticationError } from '../../common/helpers';
import { useNotifier } from '../../common/Notifications/NotificationProvider';
import GetUsersForProjectQuery from './GetUsersForProjectQuery';
import OptionsDropdown, { Option } from '../../common/Common/OptionsDropdown';
import UI from '../../common/UI';
import useAddUserModal from './useAddUserModal';
import useConfirmModal from '../../common/useConfirmModal';
import useEditUserRoleModal from './useEditUserRoleModal';
import useMediaDevice from '../../common/useMediaDevice';
import useMutation from '../../api/useMutation';
import useProject from '../useProject';
import useQuery from '../../api/useQuery';

type Project = GetUsersForProject['project'];
type User = Project['project_memberships'][0]['user'];

export const RemoveUserMutation = gql(`
  mutation RemoveUserFromProject($project_id: ID!, $user_id: ID!) {
    removeUserFromProject(
      project: { id: $project_id },
      user: { id: $user_id }
    ) {
      id
      project_memberships {
        id
        permissions
        user {
          ...UserFields
        }
      }
    }
  }
`);

interface UserOptionsDropdownProps {
  user: User;
  permissions: Permission[];
}

const UserOptionsDropdown = ({ user, permissions }: UserOptionsDropdownProps) => {
  const { t } = useTranslation();
  const { user: currentUser } = useSession();
  const notifier = useNotifier();
  const project = useProject();

  const client = useApolloClient();

  const [removeUser] = useMutation(
    RemoveUserMutation,
    {
      variables: {
        project_id: project.id,
        user_id: user.id,
      },
      onCompleted: () => {
        notifier.success(t('notifications.user_removed'));
      },
      onError: (error) => {
        if (isGraphQLAuthenticationError(error)) {
          // Manually refetch the session query if the user is no longer authorized for this project. This will trigger
          // the correct redirect.
          // Note: using Apollo's `refetchQueries` option does not work here, because refetch queries are only called
          // when a mutation completes successfully.
          client.refetchQueries({ include: [GetSessionQuery] });
        }
      },
    },
  );

  const [confirmRemove] = useConfirmModal({
    title: t('remove_user'),
    body: (
      <Trans
        i18nKey={user.id === currentUser.id ? 'confirm_remove_user_self' : 'confirm_remove_user'}
        values={{
          email: user.email,
          project: project.organisation_name,
        }}
      >
        <UI.Strong />
      </Trans>
    ),
    onConfirm: () => removeUser(),
  }, []);

  const [openEditUserRoleModal] = useEditUserRoleModal({
    user,
    permissions,
  });

  return (
    <OptionsDropdown name={user.full_name}>
      <Option
        icon={<Edit />}
        label={t('edit')}
        aria-label={t('edit_user')}
        onClick={() => openEditUserRoleModal()}
      />
      <Option
        icon={<Trash />}
        label={t('remove')}
        aria-label={t('remove_user')}
        onClick={() => confirmRemove()}
      />
    </OptionsDropdown>
  );
};

const UsersList = ({ project }: GetUsersForProject) => {
  const { t } = useTranslation();
  const { user: currentUser, userPermissions: availablePermissions } = useSession();

  const device = useMediaDevice();

  const memberships = project.project_memberships;

  const isCurrent = (user: User) => user.id === currentUser.id;

  return (
    <UI.GridContainer
      sc={{
        columns: ['1fr 145px 35px', '34px 1fr 145px 35px'],
        gutter: [0.5, 0.75],
        alignVertical: 'center',
      }}
    >
      {memberships.map(({ user, permissions, deletable }) => (
        <Fragment key={user.id}>
          {!device.isSmall && (
            <UI.Div>
              <UI.ProfilePic
                sc={{
                  background: isCurrent(user) ? 'secondary.800' : 'secondary.400',
                  fit: 'cover',
                  size: 34,
                }}
              >
                <UI.Span><UI.Span>{(user.first_name || user.email).charAt(0).toUpperCase()}</UI.Span></UI.Span>
              </UI.ProfilePic>
            </UI.Div>
          )}
          <UI.Div sc={{ muted: !user.activated }} style={{ minWidth: 0 }}>
            <UI.Truncate>
              <UI.Strong>
                {user.email}
              </UI.Strong>
            </UI.Truncate>
            <UI.Truncate>
              {user.full_name}
              {!user.activated && ` (${t('invited')})`}
            </UI.Truncate>
          </UI.Div>
          <UI.FlexContainer sc={{ alignItems: 'center', justifyContent: 'end', spaceX: 2 }}>
            <UI.Label sc={{ color: 'gray.600', small: true }} style={{ fontWeight: 500 }}>
              {permissions.includes(Permission.admin)
                ? t('administrator')
                : permissions.length === availablePermissions.length - 1
                  ? t('full_access')
                  : t('restricted_access')}
            </UI.Label>
            <UI.TooltipContainer>
              <UI.Icon sc={{ color: user.authenticator_enabled ? 'success' : 'error' }}>
                {user.authenticator_enabled ? <Shield /> : <ShieldOff />}
              </UI.Icon>
              <UI.Tooltip sc={{ alignH: 'center' }}>
                {user.authenticator_enabled ? t('2fa_enabled') : t('2fa_disabled')}
              </UI.Tooltip>
            </UI.TooltipContainer>
          </UI.FlexContainer>
          <UI.Div sc={{ textAlign: 'right' }}>
            {deletable && (
              <UserOptionsDropdown
                user={user}
                permissions={permissions}
              />
            )}
          </UI.Div>
        </Fragment>
      ))}
    </UI.GridContainer>
  );
};

interface UsersPageProps {
  projectId: string;
}

const UsersPage = ({ projectId }: UsersPageProps) => {
  const { data } = useQuery(GetUsersForProjectQuery, {
    variables: {
      project_id: projectId,
    },
  });

  const project = data?.project;

  if (!project) {
    return <></>;
  }

  return <UsersView project={project} />;
};

interface UsersViewProps {
  project: Project;
}

const UsersView = ({ project }: UsersViewProps) => {
  const { t } = useTranslation();

  const [openAddUserModal] = useAddUserModal();

  return (
    <UI.Container sc={{ compact: true, px: [0, 4] }}>
      <UI.FlexContainer sc={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <UI.H3>
          {t('users')}
        </UI.H3>

        <UI.Button
          onClick={openAddUserModal}
          sc={{ brand: 'secondary' }}
          style={{ whiteSpace: 'nowrap' }}
        >
          {t('add_user')}
        </UI.Button>
      </UI.FlexContainer>

      <UI.InputDescription sc={{ my: 3 }}>
        {t('users_description', { organisation: project.organisation_name })}
      </UI.InputDescription>

      <UI.Card sc={{ hasPanels: true }}>
        <UI.Div>
          <UsersList project={project} />
        </UI.Div>
      </UI.Card>
    </UI.Container>
  );
};

export default UsersPage;
